/**
 * Live flight status API calls (server/flight-status.js, server/flight-insights.js).
 * Every call carries the signed-in session. A failure throws; callers show
 * nothing rather than an error, since status is extra information.
 *
 * The same request made again within REUSE_MS shares the first one's answer,
 * so the legs of one trip (each asking for its booking) cost one round trip.
 */
import { supabase } from '@/lib/supabase';
import { getApiUrl } from '@/lib/apiUrls';

const REUSE_MS = 30 * 1000;
const recent = new Map();

async function fetchJson(path) {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error('Not signed in');
  const res = await fetch(getApiUrl(path), { headers: { Authorization: `Bearer ${session.access_token}` } });
  if (!res.ok) throw new Error(`Flight status unavailable (HTTP ${res.status})`);
  return res.json();
}

function get(path) {
  const hit = recent.get(path);
  if (hit && Date.now() - hit.at < REUSE_MS) return hit.promise;
  const promise = fetchJson(path);
  recent.set(path, { at: Date.now(), promise });
  promise.catch(() => recent.delete(path));
  return promise;
}

export const flightStatusApi = {
  /** { enabled, bookings: { [bookingId]: { segments } } }, live segments only. */
  batch: (bookingIds) => get(`flight-status?booking_ids=${bookingIds.map(encodeURIComponent).join(',')}`),
  /** { enabled, segments } for one trip, every flight with its window. */
  status: (bookingId) => get(`flight-status/${encodeURIComponent(bookingId)}`),
  /** { enabled, segments } with track records, the inbound plane, airports and timelines. */
  insights: (bookingId) => get(`flight-status/${encodeURIComponent(bookingId)}/insights`),
  /** Tests start clean. */
  _clear: () => recent.clear(),
};
